import axios from 'axios'
import { ElMessage } from 'element-plus'
import { getToken, removeToken } from '../utils/auth'
import router from '../router'
import { API_BASE_URL } from '../constants/env'

const request = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
})

// 请求拦截：自动加 token，无 token 时只允许登录接口
request.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = 'Bearer ' + token
    } else {
      // 无 token 时，只允许登录接口
      const url = config.url || ''
      if (!url.includes('/auth/login')) {
        return Promise.reject(new Error('NO_TOKEN'))
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截：解包 ApiResponse（Blob/ArrayBuffer 响应除外）
request.interceptors.response.use(
  (res) => {
    // 二进制响应直接返回完整 axios response（不解包）
    if (res.config.responseType === 'blob' || res.config.responseType === 'arraybuffer') {
      return res
    }
    const { code, message, data } = res.data
    if (code === 200) {
      return data
    }
    ElMessage.error(message || '请求失败')
    return Promise.reject(new Error(message))
  },
  (err) => {
    if (err.message === 'NO_TOKEN') {
      // 无 token 静默跳转登录页，不弹错误提示
      router.push('/login')
      return Promise.reject(err)
    }
    if (err.response && err.response.status === 401) {
      removeToken()
      router.push('/login')
      ElMessage.error('登录已过期，请重新登录')
    } else {
      ElMessage.error(err.message || '网络错误')
    }
    return Promise.reject(err)
  }
)

// ==================== 认证相关 ====================

/** 登录 */
export const login = (data) => request.post('/auth/login', data)

/** 登出 */
export const logout = () => request.post('/auth/logout')

/** 获取当前用户信息 */
export const getUserInfo = () => request.get('/auth/info')

/** 获取当前用户菜单 */
export const getUserMenus = () => request.get('/auth/menus')

// ==================== 用户管理 ====================

/** 用户列表 */
export const getUserList = (params) => request.get('/users', { params })

/** 新增用户 */
export const createUser = (data) => request.post('/users', data)

/** 修改用户 */
export const updateUser = (id, data) => request.put('/users/' + id, data)

/** 删除用户 */
export const deleteUser = (id) => request.delete('/users/' + id)

/** 重置密码 */
export const resetPassword = (id, data) => request.put('/users/' + id + '/reset-password', data)

/** 获取用户角色 */
export const getUserRoles = (id) => request.get('/users/' + id + '/roles')

/** 分配角色 */
export const assignRoles = (id, data) => request.post('/users/assign-roles', { userId: id, roleIds: data.roleIds })

// ==================== 菜单管理 ====================

/** 菜单树 */
export const getMenuTree = () => request.get('/menus/tree')

/** 菜单列表 */
export const getMenuList = () => request.get('/menus')

/** 新增菜单 */
export const createMenu = (data) => request.post('/menus', data)

/** 修改菜单 */
export const updateMenu = (id, data) => request.put('/menus/' + id, data)

/** 删除菜单 */
export const deleteMenu = (id) => request.delete('/menus/' + id)

/** 获取角色菜单 */
export const getRoleMenus = (roleId) => request.get('/menus/role/' + roleId)

// ==================== 角色管理 ====================

/** 角色列表 */
export const getRoleList = () => request.get('/roles')

/** 新增角色 */
export const createRole = (data) => request.post('/roles', data)

/** 修改角色 */
export const updateRole = (data) => request.put('/roles', data)

/** 删除角色 */
export const deleteRole = (id) => request.delete('/roles/' + id)

/** 分配角色菜单权限 */
export const assignMenus = (data) => request.post('/roles/assign-menus', data)

// ==================== 收单管理 ====================

/** 上传ZIP文件（大文件上传） */
export const uploadZip = (formData) =>
  request.post('/receipt/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 200000
  })

// ==================== 查询接口 ====================

/** 1. 分页查询导入文件批次 */
export const pageBatches = (params) =>
  request.get('/receipt/batches', { params })

/** 2. 通过批次号查询收单批次清单 */
export const pageBatchItems = (params) =>
  request.get('/receipt/batch-items', { params })

/** 3. 分页查询Excel文件清单 */
export const pageExcelFileLists = (params) =>
  request.get('/receipt/excel-file-lists', { params })

/** 4. 分页查询清单报告 */
export const pageListReports = (params) =>
  request.get('/receipt/list-reports', { params })

/** 5. 按清单号查订单批次 */
export const pageOrderBatches = (params) =>
  request.get('/receipt/order-batches', { params })

/** 6. 按清单号查订单列表 */
export const pageBatchOrders = (params) =>
  request.get('/receipt/batch-orders', { params })

/** 10. 按批次号查Excel文件清单 */
export const pageExcelFileListsByBatch = (batchNo, params) =>
  request.get(`/receipt/batches/${batchNo}/excel-file-lists`, { params })

/** 11. 按批次号查检验报告(支持reportBatchNo模糊筛选) */
export const pageListReportsByBatch = (batchNo, params) =>
  request.get(`/receipt/batches/${batchNo}/list-reports`, { params })

// ==================== 下载接口（Blob） ====================

/** 下载Excel文件(通过listNo) — 返回 axios response（含 headers + data Blob） */
export const downloadExcel = (listNo) =>
  request.get(`/receipt/excel-file-lists/${listNo}/download`, { responseType: 'blob' })

/** 下载检验报告PDF(通过reportId) — 返回 axios response */
export const downloadReportPdf = (reportId) =>
  request.get(`/receipt/list-reports/${reportId}/download`, { responseType: 'blob' })

/** 获取Excel文件Blob(用于预览) — 返回 ArrayBuffer */
export async function fetchExcelBuffer(listNo) {
  const res = await request.get(`/receipt/excel-file-lists/${listNo}/preview`, {
    responseType: 'arraybuffer'
  })
  return res.data // ArrayBuffer
}

/** 获取PDF文件Blob(用于预览) — 返回 Blob */
export async function fetchPdfBlob(reportId) {
  const res = await request.get(`/receipt/list-reports/${reportId}/pdf`, {
    responseType: 'blob'
  })
  return res.data // Blob
}

/** 通用：从Blob响应触发浏览器下载 */
export function saveAs(response, fallbackName = 'download') {
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
  // 从 Content-Disposition 提取文件名
  const disposition = response.headers?.['content-disposition'] || ''
  let fileName = fallbackName
  const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i)
  if (match) fileName = decodeURIComponent(match[1].replace(/"/g, ''))
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

// ==================== 日志查询 ====================

/** 分页查询系统操作日志 */
export const pageOperationLogs = (params) =>
  request.get('/logs/operations', { params })

/** 分页查询登录日志 */
export const pageLoginLogs = (params) =>
  request.get('/logs/login', { params })

/** 分页查询批次操作日志 */
export const pageBatchLogs = (params) =>
  request.get('/logs/batch', { params })

/** 分页查询Excel清单操作日志 */
export const pageExcelListLogs = (params) =>
  request.get('/logs/excel-list', { params })

// ==================== 作废操作 ====================

/** 作废收单批次 */
export const cancelBatch = (id) =>
  request.put(`/receipt/batches/${id}/cancel`)

/** 作废Excel文件清单 */
export const cancelExcelList = (id) =>
  request.put(`/receipt/excel-file-lists/${id}/cancel`)

/** 下载收单批次ZIP文件(通过batchNo) — 返回 axios response */
export const downloadBatchZip = (batchNo) =>
  request.get(`/receipt/batches/${batchNo}/download`, { responseType: 'blob' })

/** 查询异步导入任务状态 */
export const getImportTaskStatus = (batchNo) =>
  request.get(`/receipt/tasks/${batchNo}/status`)

// ==================== 删除操作（逻辑删除）====================

/** 删除收单批次（只有已作废状态才能删除） */
export const deleteBatch = (id) =>
  request.delete(`/receipt/batches/${id}`)

/** 删除Excel文件清单（只有已作废状态才能删除） */
export const deleteExcelList = (id) =>
  request.delete(`/receipt/excel-file-lists/${id}`)

/** 删除COC订单批次（只有已作废状态才能删除） */
export const deleteCocBatch = (id) =>
  request.delete(`/coc-order/batch/${id}`)

// ==================== COC订单上传/提交 ====================

/** 查询可上传清单列表 */
export const getCocAvailableLists = () =>
  request.get('/coc-order/available-lists')

/** 根据清单ID查询是否已有COC批次 */
export const getCocBatchByListId = (listId) =>
  request.get(`/coc-order/batch-by-list/${listId}`)

/** 创建订单批次 */
export const createCocBatch = (listId) =>
  request.post('/coc-order/batch', { listId })

/** 执行上传（异步） */
export const uploadCocBatch = (id) =>
  request.post(`/coc-order/batch/${id}/upload`)

/** 执行提交（异步） */
export const submitCocBatch = (id) =>
  request.post(`/coc-order/batch/${id}/submit`)

/** 查询上传/提交进度 */
export const getCocProgress = (id) =>
  request.get(`/coc-order/batch/${id}/progress`)

/** 分页查询批次 */
export const pageCocBatches = (params) =>
  request.get('/coc-order/batch/page', { params })

/** 查询批次详情 */
export const getCocBatch = (id) =>
  request.get(`/coc-order/batch/${id}`)

/** 查询订单列表 */
export const pageCocOrders = (params) =>
  request.get('/coc-order/page', { params })

/** 上传单条订单（支持待上传、失败、超时状态，同步调JQMS API需较长超时） */
export const uploadCocOrder = (id) =>
  request.post(`/coc-order/${id}/upload-order`, null, { timeout: 120000 })

/** 提交单条订单（支持未提交、失败、超时状态，同步调JQMS API需较长超时） */
export const submitCocOrder = (id) =>
  request.post(`/coc-order/${id}/submit-order`, null, { timeout: 120000 })

/** 查询订单操作日志 */
export const getCocOrderLogs = (id) =>
  request.get(`/coc-order/${id}/logs`)

/** 撤回上传（单个订单，同步调JQMS删除API需较长超时） */
export const revokeCocUpload = (id) =>
  request.post(`/coc-order/${id}/revoke-upload`, null, { timeout: 120000 })

/** 批量撤回上传 */
export const revokeCocBatchUpload = (batchId) =>
  request.post(`/coc-order/batch/${batchId}/revoke-upload`)

/** 作废订单批次 */
export const cancelCocBatch = (id) =>
  request.post(`/coc-order/batch/${id}/cancel`)

// ========== 统一批量操作 ==========

/** 批量上传订单（支持选中或全部） */
export const batchCocUpload = (batchId, data) =>
  request.post(`/coc-order/batch/${batchId}/batch-upload`, data || {})

/** 批量撤回订单（支持选中或全部） */
export const batchCocRevoke = (batchId, data) =>
  request.post(`/coc-order/batch/${batchId}/batch-revoke`, data || {})

/** 批量提交订单（支持选中或全部） */
export const batchCocSubmit = (batchId, data) =>
  request.post(`/coc-order/batch/${batchId}/batch-submit`, data || {})

/** 批量作废订单（支持选中或全部） */
export const batchCocCancel = (batchId, data) =>
  request.post(`/coc-order/batch/${batchId}/cancel-orders`, data || {})

/** 查询批次操作日志 */
export const getCocBatchLogs = (batchId) =>
  request.get(`/coc-order/batch/${batchId}/logs`)

// ========== 校验接口 ==========

/** 校验批量上传 */
export const validateBatchCocUpload = (batchId, data) =>
  request.post(`/coc-order/batch/${batchId}/validate-upload`, data || {})

/** 校验批量撤回 */
export const validateBatchCocRevoke = (batchId, data) =>
  request.post(`/coc-order/batch/${batchId}/validate-revoke`, data || {})

/** 校验批量提交 */
export const validateBatchCocSubmit = (batchId, data) =>
  request.post(`/coc-order/batch/${batchId}/validate-submit`, data || {})

// ========== COC快照相关 ==========

/** 查询订单最新快照 */
export const getCocSnapshot = (orderId) =>
  request.get(`/coc-order/${orderId}/snapshot`)

/** 编辑保存快照 */
export const updateCocSnapshot = (orderId, data) =>
  request.put(`/coc-order/${orderId}/snapshot`, data)

/** 查询订单快照历史 */
export const getCocSnapshotHistory = (orderId) =>
  request.get(`/coc-order/${orderId}/snapshot/history`)

/** 查询批次快照生成状态 */
export const getBatchSnapshotStatus = (batchId) =>
  request.get(`/coc-order/batch/${batchId}/snapshot-status`)

// ========== COC同步配置管理 ==========

export const getCocSyncConfig = () =>
  request.get('/coc-sync-config')

export const updateCocSyncConfig = (data) =>
  request.put('/coc-sync-config', data)

// ========== Token 管理 ==========

export const getTokenStatus = () =>
  request.get('/coc-sync-config/token')

export const refreshToken = () =>
  request.post('/coc-sync-config/token/refresh')

export const setToken = (data) =>
  request.post('/coc-sync-config/token/set', data)

// 导出 request 实例供其他模块使用
export default request
